import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { useSelector } from 'react-redux';
import { selectUserType } from '../../store/slices/userSlice';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import Profile from './profile/Profile';
import DSPs from './dsp/Dsp';
import PitchPack from './pitch-pack/PitchPack';
import SubscriptionDetails from './subscription-details/SubscriptionDetails';
import Privacy from './privacy/Privacy';
import Support from './support/Support';
import Billing from './billing/Billing';
import Token from './token/Token';
import SegmentedTabs from '../ui/SegmentedTabs';

interface TabsProps {
  initialTab?: string | null;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: any) => void;
}

const Tabs: React.FC<TabsProps> = ({ initialTab, searchParams, setSearchParams }) => {
  const { user } = useAppSelector((state) => state.auth);
  const userType = useSelector(selectUserType);
  const { headless } = useUserPermissions();

  const hasHeadlessAccess = !!headless && headless.limit === null;

  const getAvailableTabs = () => {
    if (user?.role === 'admin') {
      return ['profile', 'api-tokens'];
    }

    if (user?.role === 'dsp') {
      const baseTabs = ['profile', 'privacy', 'support'];
      // Show API tokens only if headless permission exists
      if (hasHeadlessAccess) {
        return [...baseTabs, 'api-tokens'];
      }
      return baseTabs;
    }

    if (userType === 'subscription') {
      const baseTabs = ['profile', 'dsps', 'subscription-details', 'privacy', 'support'];
      // Show API tokens only if user has headless permission
      if (hasHeadlessAccess) {
        return [...baseTabs, 'api-tokens'];
      }
      return baseTabs;
    }

    // Paid (pitch-pack) users always get api-tokens
    return ['profile', 'dsps', 'pitch-pack', 'privacy', 'support', 'billing', 'api-tokens'];
  };

  const availableTabs = getAvailableTabs();

  // Get initial tab from URL or default
  const getInitialActiveTab = (): string => {
    const urlTab = searchParams?.get('tab');
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }
    
    if (initialTab && availableTabs.includes(initialTab)) {
      return initialTab;
    }
    
    return availableTabs[0] || 'profile';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialActiveTab());

  // Update active tab when URL changes
  useEffect(() => {
    const urlTab = searchParams?.get('tab');
    
    if (urlTab && availableTabs.includes(urlTab)) {
      setActiveTab(urlTab);
    } else if (initialTab && availableTabs.includes(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [searchParams, initialTab, availableTabs]);

  const handleTabClick = (tabKey: string) => {
    setActiveTab(tabKey);
    
    if (setSearchParams) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tabKey);
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const tabConfig = [
    { name: 'Profile', key: 'profile' },
    { name: 'DSPs', key: 'dsps' },
    { name: 'Pitch Pack', key: 'pitch-pack' },
    { name: 'Subscription Details', key: 'subscription-details' },
    { name: 'Privacy', key: 'privacy' },
    { name: 'Support', key: 'support' },
    { name: 'Billing', key: 'billing' },
    { name: 'API Tokens', key: 'api-tokens' },
  ];

  const filteredTabConfig = tabConfig.filter(tab => availableTabs.includes(tab.key));

  // Render component based on active tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'profile':
        return <Profile />;
      case 'dsps':
        return <DSPs />;
      case 'pitch-pack':
        return <PitchPack onContactSupport={() => handleTabClick('support')} />;
      case 'subscription-details':
        return <SubscriptionDetails />;
      case 'privacy':
        return <Privacy />;
      case 'support':
        return <Support />;
      case 'billing':
        return <Billing />;
      case 'api-tokens':
        return <Token />;
      default:
        return <Profile />;
    }
  };

  return (
    <div className="w-full overflow-hidden">
      {/* Tabs Navigation — the shared underline pattern used by every other tab strip */}
      <SegmentedTabs
        items={filteredTabConfig.map((tab) => ({ key: tab.key, label: tab.name }))}
        value={activeTab}
        onChange={handleTabClick}
        ariaLabel="Settings sections"
        className="overflow-x-auto scrollbar-hide"
      />

      {/*
        One width for every tab, owned here rather than by each panel. The tabs
        had picked five different caps between them (3xl/4xl/5xl/6xl/none), so
        the card visibly resized whenever you switched tabs. Individual panels
        no longer set their own max-width - change this one line to re-cap them
        all.
      */}
      <div className="w-full mt-4 sm:mt-6 overflow-x-hidden">
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default Tabs;